import { isValidCnpj } from '@/helpers/validators/cnpj'

export const FISCAL_STATES = Object.freeze({
  draft: 'Rascunho', preview: 'Preview', reserved: 'Reservada', processing: 'Processando',
  authorized: 'Autorizada', rejected: 'Rejeitada', cancel_requested: 'Cancelamento solicitado',
  cancelled: 'Cancelada', unknown: 'Desconhecida — Reconciliação pendente',
})
const LEGACY_STATES = { autorizada: 'authorized', autorizado: 'authorized', rejeitada: 'rejected', rejeitado: 'rejected', cancelada: 'cancelled', cancelado: 'cancelled', pendente: 'processing', processando: 'processing', contingencia: 'unknown' }
export function normalizeFiscalStatus(status) {
  const value = String(status || '').trim().toLowerCase()
  return Object.prototype.hasOwnProperty.call(FISCAL_STATES, value) ? value : (LEGACY_STATES[value] || 'unknown')
}
export const fiscalStatusLabel = status => FISCAL_STATES[normalizeFiscalStatus(status)]
export const fiscalStatusVariant = status => ({ authorized: 'success', rejected: 'danger', cancelled: 'secondary', unknown: 'danger', preview: 'info' }[normalizeFiscalStatus(status)] || 'warning')
export const isPreparation = operation => ['draft', 'preview', 'reserved'].includes(normalizeFiscalStatus(operation?.status))
export const needsReconciliation = operation => normalizeFiscalStatus(operation?.status) === 'unknown' || Boolean(operation?.reconciliation_required_at)

const positiveId = value => Number.isSafeInteger(Number(value)) && Number(value) > 0
export const validOperationVersion = value => ['number', 'string'].includes(typeof value) && positiveId(value)
const dateValid = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '') && !Number.isNaN(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value
export function buildEvolutionFilters(filters = {}) {
  if (!dateValid(filters.date_ini) || !dateValid(filters.date_end)) throw new Error('Informe um período com data inicial e final válidas.')
  if (filters.date_ini > filters.date_end) throw new Error('Período inválido: a data final deve ser igual ou posterior à inicial.')
  if (!positiveId(filters.agreement_id)) throw new Error('Selecione um convênio.')
  if (!['all', 'yes', 'no'].includes(filters.nfse_generated)) throw new Error('Selecione a situação de NFS-e: todos, sim ou não.')
  const result = { date_ini: filters.date_ini, date_end: filters.date_end, agreement_id: Number(filters.agreement_id), nfse_generated: filters.nfse_generated, page: Number(filters.page || 1), per_page: Number(filters.per_page || 25) }
  if (!positiveId(result.page) || !positiveId(result.per_page)) throw new Error('Paginação inválida.')
  for (const key of ['professional_id', 'pacient_id', 'specialty_id']) {
    if (filters[key] !== '' && filters[key] != null) {
      if (!positiveId(filters[key])) throw new Error('Identificador de filtro inválido.')
      result[key] = Number(filters[key])
    }
  }
  return result
}

export function createManualItem(clientItemId = 'item-1') {
  return {
    client_item_id: clientItemId, descricao: '', codigo_servico_municipal: '', item_lista_servico: '',
    quantidade: '1', valor_unitario: '', desconto: '0.00',
  }
}
export function createManualDraft() {
  return {
    competencia: '',
    tomador: {
      tipo: 'pessoa_fisica', documento: { tipo: 'cpf', numero: '' }, nome: '', email: '',
      endereco: { logradouro: '', numero: '', bairro: '', municipio_codigo_ibge: '', uf: '', cep: '' },
    },
    itens: [createManualItem()],
  }
}
function validCpf(value) {
  if (!/^\d{11}$/.test(value) || /^(\d)\1{10}$/.test(value)) return false
  return [9, 10].every(length => {
    const sum = value.slice(0, length).split('').reduce((total, digit, index) => total + Number(digit) * (length + 1 - index), 0)
    return ((sum * 10) % 11) % 10 === Number(value[length])
  })
}
// Apenas validação de entrada. Cálculo tributário, vigência e arredondamento pertencem à API.
export function decimalInput(value) {
  const raw = String(value ?? '').trim()
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw
  return /^\d+(\.\d+)?$/.test(normalized) && Number.isFinite(Number(normalized)) ? normalized : null
}
export function validateManualDraft(draft = {}) {
  const errors = {}
  const tomador = draft.tomador || {}
  const document = tomador.documento || {}
  const documentNumber = String(document.numero || '').replace(/\D/g, '')
  const documentValid = document.tipo === 'cpf' ? validCpf(documentNumber) : document.tipo === 'cnpj' && isValidCnpj(documentNumber)
  if (!tomador.nome?.trim() || !documentValid) errors.tomador = 'Informe nome e CPF/CNPJ válido do tomador fiscal.'
  const address = tomador.endereco || {}
  if (!['logradouro', 'numero', 'bairro', 'municipio_codigo_ibge', 'uf', 'cep'].every(key => String(address[key] || '').trim())
    || !/^\d{7}$/.test(address.municipio_codigo_ibge || '') || !/^[A-Za-z]{2}$/.test(address.uf || '')
    || !/^\d{8}$/.test(String(address.cep || '').replace(/\D/g, ''))) {
    errors.endereco = 'Preencha o endereço fiscal, CEP, UF e código IBGE do tomador.'
  }
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(draft.competencia || '') && !/^\d{4}-(0[1-9]|1[0-2])-01$/.test(draft.competencia || '')) errors.competencia = 'Informe a competência.'
  if (!Array.isArray(draft.itens) || !draft.itens.length || draft.itens.some(item => !String(item.client_item_id || '').trim()
    || !item.descricao?.trim() || !String(item.codigo_servico_municipal || '').trim()
    || !String(item.item_lista_servico || '').trim() || Number(decimalInput(item.quantidade)) <= 0
    || Number(decimalInput(item.valor_unitario)) <= 0 || decimalInput(item.desconto) === null)
    || new Set(draft.itens.map(item => String(item.client_item_id).trim())).size !== draft.itens.length) {
    errors.itens = 'Informe descrição, códigos fiscais, quantidade, valor positivo e desconto válido para cada item.'
  }
  return errors
}
export function buildManualPreview(draft) {
  const errors = validateManualDraft(draft)
  if (Object.keys(errors).length) throw new Error(Object.values(errors).join(' '))
  const { nome, email, documento, endereco } = draft.tomador
  const competencia = /^\d{4}-\d{2}$/.test(draft.competencia) ? `${draft.competencia}-01` : draft.competencia
  return {
    mode: 'manual', version: 1, competencia,
    tomador: {
      tipo: documento.tipo === 'cnpj' ? 'pessoa_juridica' : 'pessoa_fisica',
      documento: { tipo: documento.tipo, numero: String(documento.numero).replace(/\D/g, '') },
      nome: nome.trim(), email: email?.trim() || null,
      endereco: {
        logradouro: endereco.logradouro.trim(), numero: endereco.numero.trim(), bairro: endereco.bairro.trim(),
        municipio_codigo_ibge: endereco.municipio_codigo_ibge, uf: endereco.uf.toUpperCase(), cep: endereco.cep.replace(/\D/g, ''),
      },
    },
    itens: draft.itens.map(item => ({
      client_item_id: String(item.client_item_id).trim(), descricao: item.descricao.trim(),
      codigo_servico_municipal: String(item.codigo_servico_municipal).trim(), item_lista_servico: String(item.item_lista_servico).trim(),
      quantidade: decimalInput(item.quantidade), valor_unitario: decimalInput(item.valor_unitario), desconto: decimalInput(item.desconto),
    })),
  }
}
export function contextBelongsTo(operation, scope) {
  // A resposta manual aprovada omite user_id; empresa continua obrigatória.
  const userMatches = operation?.mode === 'manual' && !Object.prototype.hasOwnProperty.call(operation, 'user_id')
    || String(operation?.user_id) === String(scope.userId)
  return Boolean(operation?.operation_id && scope.empresaId && scope.userId && String(operation.empresa_id) === String(scope.empresaId) && userMatches)
}
export function contextIsValid(operation, scope, now = Date.now()) {
  return contextBelongsTo(operation, scope) && validOperationVersion(operation.version) && Date.parse(operation.expires_at) > now
}
export function normalizeNfseGroup(group = {}) {
  const taxes = group.taxes || {}
  return {
    ...group,
    service_code: group.service_code ?? group.code_tax_id ?? '',
    discounts: group.discounts ?? group.deductions ?? null,
    tax_base: group.tax_base ?? group.base ?? null,
    taxes: {
      ...taxes,
      iss_due: taxes.iss_due ?? group.iss_due ?? group.tax_due ?? null,
      iss_retained: taxes.iss_retained ?? group.iss_retained ?? group.withheld ?? null,
    },
    status: normalizeFiscalStatus(group.status),
  }
}
export function normalizeOperation(operation) {
  return { ...operation, status: normalizeFiscalStatus(operation.status), groups: Array.isArray(operation.groups) ? operation.groups.map(normalizeNfseGroup) : [] }
}
export function canPreviewOperation(operation) {
  return isPreparation(operation) && !needsReconciliation(operation) && !hasFiscalBlockingErrors(operation)
    && (operation.groups == null || Array.isArray(operation.groups))
    && (operation.groups || []).every(group => isPreparation(group) && !needsReconciliation(group) && !hasFiscalBlockingErrors(group))
}
export function hasFiscalBlockingErrors(data) {
  return data?.code === 'fiscal_configuration_unavailable'
    || [data?.errors, data?.blocking_errors].some(errors => Boolean(errors && Object.keys(errors).length))
}
export function completePreview(operation) {
  if (normalizeFiscalStatus(operation?.status) !== 'preview' || hasFiscalBlockingErrors(operation)
    || needsReconciliation(operation) || !Array.isArray(operation?.groups) || !operation.groups.length) return false
  if (operation.groups.some(group => !String(group?.group_id ?? '').trim() || group.status !== 'preview'
    || hasFiscalBlockingErrors(group) || needsReconciliation(group))
    || new Set(operation.groups.map(group => String(group.group_id))).size !== operation.groups.length) return false
  if (operation.mode === 'manual') {
    return operation.groups.every(group => validOperationVersion(group.fiscal_configuration_version) && positiveId(group.item_count)
      && ['gross', 'discounts', 'tax_base', 'iss_due', 'iss_retained', 'net'].every(key => decimalInput(group[key]) !== null))
  }
  return operation.total != null && decimalInput(operation.total) !== null && Number(operation.total) > 0
}
export function createIdempotencyKey() {
  if (!window.crypto?.getRandomValues) throw new Error('Identificação segura indisponível neste navegador.')
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}
export function formatFiscalMoney(value) {
  if (value === '' || value == null || !Number.isFinite(Number(value))) return 'Não informado'
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}
